"use client";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "react-toastify";
import {
  useCompanyInfo,
  useGetCityDataMutation,
  useUpdateCompanyInfo,
  useCompanyUpdateLogo,
  useCompanydetails,
} from "../hooks/useCommon";
import { useCity } from "../store/common";
import { useMemo, useState, useEffect } from "react";
import { debounce } from "lodash";
import Link from "next/link";

const companyFormSchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  company_address: z.string().min(1, "Office address is required"),
  // city_id: z.string().min(1, "City is required"),
  contact_person_name: z.string().min(3, "Contact person is required"),
  mobile_no: z
    .string()
    .min(10, "Minimum 10 digits")
    .regex(/^[6-9]\d{9}$/, "Invalid mobile number"),
  service_tax_gst: z.string().optional(),
  pancard_no: z.string().min(10, "Minimum 10 digits"),
  landline_no: z.string().optional(),
  email: z.string().email("Invalid email").optional(),
  website_url: z
    .string()
    .trim()
    .optional()
    .refine(
      (val) => {
        if (!val || val === "") return true;

        // allow domain.com, www.domain.com, http(s)://domain.com
        const domainRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/;

        return domainRegex.test(val);
      },
      {
        message: "Invalid website URL",
      }
    )
    .transform((val) => (val === "" ? undefined : val)),

  pincode: z.string().optional(),
  user_email: z.string().optional(),
  user_mobile: z.string().optional(),
  typeId: z.string().optional(),
  id: z.string().optional(),
  active: z.string().optional(),
  center: z.string().optional(),
  center_code: z.string().optional(),
  constitution: z.string().optional(),
  einvoice_enabled: z.string().optional(),
  expiry_date: z.string().optional(),
  filings: z.string().optional(),
  gst_type: z.string().optional(),
  last_updated_on: z.string().optional(),
  legal_name: z.string().optional(),
  nature_of_business: z.string().optional(),
  primary_address: z.string().optional(),
  registered_on: z.string().optional(),
  state: z.string().optional(),
  state_code: z.string().optional(),
  trade_name: z.string().optional(),
  company_logo: z.union([z.string(), z.object({})]).optional(),
  company_pancard_image: z.union([z.string(), z.object({})]).optional(),
  is_active: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companyFormSchema>;

// const inputClass =
//   "w-full h-9 px-3 text-xs text-gray-800 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#dfad08] focus:border-[#dfad08]";

// const disabledInputClass =
//   "w-full h-9 px-3 text-xs text-gray-500 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed";

// const labelClass = "block text-xs font-medium text-gray-700";

const CompanyRegistrationForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companyFormSchema),
  });

  const router = useRouter();
  const companyProfileMutation = useCompanyInfo();
  const { mutate: getCityData } = useGetCityDataMutation();
  const { cityData } = useCity();
  const { mutate: getCompanyData } = useCompanydetails();

  const [cityInput, setCityInput] = useState("");
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cData, setCData] = useState<Partial<CompanyFormData> | null>(null);

  const searchParams = useSearchParams();
  const user_email = searchParams.get("email");
  const user_mobile = searchParams.get("mobile");
  const typeId = searchParams.get("typeId");
  const redirect = searchParams.get("redirect");

  const companyDetailMutation = useUpdateCompanyInfo();
  const { mutate: uploadPhoto } = useCompanyUpdateLogo(cData?.id || 0);
  const debouncedGetCity = useMemo(() => {
    return debounce((val: string) => {
      if (val) getCityData(val);
    }, 500);
  }, [getCityData]);

  const handleCityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCityInput(value);
    setShowSuggestions(true);
    debouncedGetCity(value);
  };

  const handleCitySelect = (name: string, id: string) => {
    setCityInput(name);
    setSelectedCityId(id);
    setShowSuggestions(false);
  };

  const onSubmit = (data: CompanyFormData) => {
    const payload = {
      ...data,
      user_email: user_email || "",
      user_mobile: user_mobile || "",
      typeId: typeId || "",
      pincode: data.pincode || "",
      service_tax_gst: data.service_tax_gst || "",
      pancard_no: data.pancard_no || "",
      email: data.email || "",
      landline_no: data.landline_no || "",
      website_url: data.website_url || "",
      // city_id: selectedCityId,
      id: cData?.id || 0,
    };

    console.log(payload);

    companyDetailMutation.mutate(payload, {
      onSuccess: (res: any) => {
        const redirectTo =
          res?.data?.responseData?.response?.data?.responsePayload?.redirectTo;
        console.log(redirectTo);

        toast.success("Company info added successfully!");
        if (typeId == "3") {
          if (redirectTo) {
            router.push(redirectTo);
          } else {
            router.push(
              `/vehicle-inventory?email=${decodeURIComponent(user_email)}`
            );
          }
        } else {
          router.push(`/`);
        }
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.responseData?.response?.message ||
            "Company info adding failed"
        );
      },
    });
  };
  const handleLogoImageChange = async (e: any, type: any) => {
    const file = e.target.files?.[0];
    if (file) {
      const uploadedUrl = await uploadPhoto({ file, folder: type });

      if (type === "company_logo") {
        setValue("company_logo", "abc");
      } else if (type === "company_pan") {
        setValue("company_pancard_image", "abc");
      }
    }
  };

  const onVerifyHandler = () => {
    toast.info("Pan card verifed successfully.", {
      position: "top-right",
      autoClose: 5000,
    });
  };

  useEffect(() => {
    const storedData = localStorage.getItem("companyDetail");

    if (!storedData) return;

    try {
      const parsed = JSON.parse(storedData);

      console.log("parsed", parsed);

      if (parsed?.company_name || parsed?.service_tax_gst) {
        reset({
          company_name: parsed.company_name ?? "",
          company_address: parsed.company_address ?? "",
          pincode: parsed.pincode ?? "",
          contact_person_name: parsed.contact_person_name ?? "",
          service_tax_gst: parsed.service_tax_gst ?? "",
          landline_no: parsed.landline_no ?? "",
          mobile_no: parsed.mobile_no ?? "",
          email: parsed.email ?? "",
          website_url: parsed.website_url ?? "",
          pancard_no: parsed.pancard_no ?? "",
        });
      }
    } catch (err) {
      console.error("Failed to parse companyDetail from localStorage", err);
    }
  }, [reset]);

  useEffect(() => {
    if (!redirect || !user_email) return;

    let company = getCompanyData(
      { email: user_email },
      {
        onSuccess: (res: any) => {
          const company = Array.isArray(res) ? res?.[0] : res;

          console.log("Redirect company", company);

          if (!company) return;

          setCData(company); // keep existing logic

          reset({
            company_name: company.company_name ?? "",
            company_address: company.company_address ?? "",
            pincode: company.pincode ?? "",
            contact_person_name: company.contact_person_name ?? "",
            service_tax_gst: company.service_tax_gst ?? "",
            landline_no: company.landline_no ?? "",
            mobile_no: company.mobile_no ?? "",
            email: company.email ?? "",
            website_url: company.website_url ?? "",
            pancard_no: company.pancard_no ?? "",
            active: company.active ?? "",
            center: company.center ?? "",
            center_code: company.center_code ?? "",
            constitution: company.constitution ?? "",
            einvoice_enabled: company.einvoice_enabled ?? "",
            expiry_date: company.expiry_date ?? "",
            filings: company.filings ?? "",
            gst_type: company.gst_type ?? "",
            legal_name: company.legal_name ?? "",
            nature_of_business: company.nature_of_business ?? "",
            primary_address: company.primary_address ?? "",
            state: String(company.state) ?? "",
            state_code: company.state_code ?? "",
            trade_name: company.trade_name ?? "",
          });
        },
      }
    );
  }, [redirect, user_email, getCompanyData, reset]);

  useEffect(() => {
    if (cData) {
      console.log("Company data loaded:", cData?.[0]);
      localStorage.setItem("companyDetail", JSON.stringify(cData?.[0]));
    }
  }, [cData]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-7xl shadow-lg rounded-sm mx-auto bg-white p-8 space-y-6 bg-white"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        {/* Company Name */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Company Name <span className="text-red-600">*</span>
          </label>
          <input
            {...register("company_name")}
            placeholder="Type Company Name"
            disabled
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.company_name && (
            <p className="text-xs text-red-500 mt-1">
              {errors.company_name.message}
            </p>
          )}
        </div>

        {/* Office Address */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Office Address <span className="text-red-600">*</span>
          </label>
          <input
            {...register("company_address")}
            placeholder="Type Office Address"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.company_address && (
            <p className="text-xs text-red-500 mt-1">
              {errors.company_address.message}
            </p>
          )}
        </div>

        {/* Pin Code */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">Pin Code</label>
          <input
            {...register("pincode")}
            placeholder="Type Pin Code"
            className="text-black  text-xs font-normal w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.pincode && (
            <p className="text-xs text-red-500 mt-1">
              {errors.pincode.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label className="block text-xs text-gray-800">
            VAT/GST <span className="text-red-600">*</span>
          </label>
          <input
            {...register("service_tax_gst")}
            placeholder="Type GST/VAT No."
            disabled
            className="text-black  text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.service_tax_gst && (
            <p className="text-xs text-red-500 mt-1">
              {errors.service_tax_gst.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1 py-1">
          <label className="block text-xs  text-gray-800">Company Logo</label>
          <input
            {...register("company_logo")}
            onChange={(e) => {
              handleLogoImageChange(e, "company_logo");
            }}
            accept="image/*"
            type="file"
            className="text-black text-xs  w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {/* {errors.kycUpload && (
              <p className="text-xs text-red-600 mt-[1px]">
                {errors.kycUpload.message}
              </p>
            )} */}
        </div>

        {/* Pin Code */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Contact Person <span className="text-red-600">*</span>
          </label>
          <input
            {...register("contact_person_name")}
            placeholder="Type Contact person"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.contact_person_name && (
            <p className="text-xs text-red-500 mt-1">
              {errors.contact_person_name.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Landline Number <span className="text-red-600">*</span>
          </label>
          <input
            {...register("landline_no")}
            placeholder="Type landline No"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.landline_no && (
            <p className="text-xs text-red-500 mt-1">
              {errors.landline_no.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Mobile Number <span className="text-red-600">*</span>
          </label>
          <input
            {...register("mobile_no")}
            placeholder="Type Mobile No"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.mobile_no && (
            <p className="text-xs text-red-500 mt-1">
              {errors.mobile_no.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        {/* Company Name */}

        {/* Office Address */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Company Email <span className="text-red-600">*</span>
          </label>
          <input
            {...register("email")}
            placeholder="user@domain.com"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Pin Code */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">Website URL</label>
          <input
            {...register("website_url")}
            placeholder="https://yourdomain.com"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">GST Status</label>
          <input
            type="number"
            {...register("active")}
            placeholder="status"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">GST Center</label>
          <input
            {...register("center")}
            placeholder="Type center"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            GST Center Code
          </label>
          <input
            {...register("center_code")}
            placeholder="Type Code"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>

        {/* Pin Code */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            GST Constitution
          </label>
          <input
            {...register("constitution")}
            placeholder="Enter GST Constitution"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            GST einvoice_enabled
          </label>
          <input
            type="number"
            {...register("einvoice_enabled")}
            placeholder="status"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            GST expiry_date
          </label>
          <input
            type="date"
            {...register("expiry_date")}
            placeholder="Type center"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">GST Filings</label>
          <input
            {...register("filings")}
            placeholder="Type filings"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>

        {/* Pin Code */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">GST Type</label>
          <input
            {...register("gst_type")}
            placeholder=""
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            GST last updated on
          </label>
          <input
            {...register("last_updated_on")}
            placeholder=""
            disabled
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">Legal Name</label>
          <input
            {...register("legal_name")}
            placeholder=""
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Nature of business
          </label>
          <input
            {...register("nature_of_business")}
            placeholder=""
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>

        {/* Pin Code */}
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Primary Address
          </label>
          <input
            {...register("primary_address")}
            placeholder=""
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            GST Registered_on
          </label>
          <input
            {...register("registered_on")}
            placeholder=""
            disabled
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">GST state</label>
          <input
            {...register("state")}
            placeholder="Type center"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">State_code</label>
          <input
            {...register("state_code")}
            placeholder=""
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">GST Trade Name</label>
          <input
            {...register("trade_name")}
            placeholder=""
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs  text-gray-800">
            Pan Card No <span className="text-red-600">*</span>
          </label>
          <input
            {...register("pancard_no")}
            disabled={cData?.is_active == "1"}
            placeholder="Pan Card Details"
            className="text-black text-xs w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
          {cData?.is_active != "1" && (
            <button
              onClick={onVerifyHandler}
              type="button"
              className="bg-[#dfad08] ml-1 px-3 py-2 cursor-pointer rounded text-[10px] font-[500]"
            >
              Verify
            </button>
          )}
          {errors.pancard_no && (
            <p className="text-xs text-red-500 mt-1">
              {errors.pancard_no.message}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1 py-1">
          <label className="block text-xs  text-gray-800">Pan Image</label>
          <input
            {...register("company_pancard_image")}
            type="file"
            accept="image/*"
            onChange={(e) => {
              handleLogoImageChange(e, "company_pan");
            }}
            className="text-black text-xs  w-full h-8 px-4 py-2 border border-gray-300 rounded placeholder-gray-500 focus:outline-none"
          />
        </div>
      </div>
      <div className="flex flex-col items-center justify-center w-full gap-5">
        <button
          type="submit"
          className="w-full font-[500] bg-[#dfad08] hover:bg-[#9d7a20] cursor-pointer py-3 px-4 rounded-md  transition text-xs"
        >
          Submit
        </button>

        <Link
          className="text-center underline text-blue-600 text-xs sm:text-sm"
          href={`/vehicle-details?email=${encodeURIComponent(user_email)}`}
        >
          Skip
        </Link>
      </div>
    </form>
  );
};

export default CompanyRegistrationForm;
